import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, BarChart3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSettings } from '@/context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ReturnsAnalyticsDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { formatCurrency } = useSettings();

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/v1/returns/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load returns analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <BarChart3 className="w-5 h-5 text-purple-400" /> Returns Analytics
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>
        ) : (
          <div className="space-y-5" data-testid="returns-analytics-content">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-zinc-400">Total Returns</p>
                <p className="text-xl font-bold" data-testid="analytics-total-returns">{data.total_returns}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-zinc-400">Refund Value (completed)</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(data.total_refund_value || 0)}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2 text-zinc-300">Monthly return rate</p>
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500 border-b border-white/10">
                  <th className="text-left py-1">Month</th><th className="text-right">Returns</th>
                  <th className="text-right">Delivered</th><th className="text-right">Rate</th>
                  <th className="text-right">Refund Value</th>
                </tr></thead>
                <tbody>
                  {(data.monthly || []).map((m) => (
                    <tr key={m.month} className="border-b border-white/5">
                      <td className="py-1.5">{m.month}</td>
                      <td className="text-right">{m.returns}</td>
                      <td className="text-right text-zinc-400">{m.delivered_orders}</td>
                      <td className="text-right font-semibold">{m.return_rate_pct != null ? `${m.return_rate_pct}%` : '—'}</td>
                      <td className="text-right text-red-300">{formatCurrency(m.refund_value || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold mb-2 text-zinc-300">By reason</p>
                <ul className="text-xs space-y-1">
                  {Object.entries(data.by_reason || {}).slice(0, 8).map(([r, c]) => (
                    <li key={r} className="flex justify-between"><span className="text-zinc-400 capitalize">{r.replace(/_/g, ' ')}</span><span className="font-semibold">{c}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2 text-zinc-300">Top returned SKUs</p>
                <ul className="text-xs space-y-1">
                  {(data.top_skus || []).map((s) => (
                    <li key={s.sku} className="flex justify-between"><span className="text-zinc-400">{s.sku}</span><span className="font-semibold">{s.quantity} pcs</span></li>
                  ))}
                  {(data.top_skus || []).length === 0 && <li className="text-zinc-500">No data</li>}
                </ul>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2 text-zinc-300">By status</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(data.by_status || {}).map(([s, c]) => (
                  <span key={s} className="px-2 py-1 rounded-full bg-white/10 capitalize">{s.replace(/_/g, ' ')}: <b>{c}</b></span>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReturnsAnalyticsDialog;
